/**
 * DeviceMotionSource — phone acceleration + shake detection.
 *
 * Wraps the W3C `devicemotion` event. Pushes:
 *
 *   - `accelX` / `accelY` / `accelZ` axes (normalized to [-1, +1] over a
 *     ±20 m/s² range, source name `'motion'`)
 *   - synthetic `'shake'` action: when total acceleration magnitude crosses
 *     `shakeThreshold`, the action latches `true` for `shakeHoldMs`, then
 *     auto-clears. Lets game code do `if (input.action('shake')) jump()`.
 *
 * iOS 13+ requires a user gesture to grant access. Call `requestPermission()`
 * from a click/tap handler before `install()`.
 */
export class DeviceMotionSource {
  /**
   * @param {object} opts
   * @param {import('./Input.js').Input} opts.input
   * @param {number} [opts.shakeThreshold=18]   - m/s² magnitude that triggers a shake.
   * @param {number} [opts.shakeHoldMs=120]     - how long the action stays latched.
   * @param {number} [opts.shakeCooldownMs=300] - minimum gap between shakes.
   */
  constructor(opts) {
    this.input = opts.input;
    this.shakeThreshold = opts.shakeThreshold || 18;
    this.shakeHoldMs = opts.shakeHoldMs || 120;
    this.shakeCooldownMs = opts.shakeCooldownMs || 300;

    this._installed = false;
    this._handler = null;
    this._lastShakeAt = 0;
    this._shakeReleaseTimer = null;

    this._latest = { x: 0, y: 0, z: 0, magnitude: 0 };
  }

  async requestPermission() {
    if (typeof window === 'undefined') return 'unsupported';
    const DME = window.DeviceMotionEvent;
    if (!DME) return 'unsupported';
    if (typeof DME.requestPermission === 'function') {
      try {
        return await DME.requestPermission();
      } catch (_) {
        return 'denied';
      }
    }
    return 'granted';
  }

  install() {
    if (this._installed) return;
    if (typeof window === 'undefined') return;
    this._installed = true;
    this._handler = (ev) => this._onEvent(ev);
    window.addEventListener('devicemotion', this._handler, true);
  }

  destroy() {
    if (!this._installed) return;
    this._installed = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('devicemotion', this._handler, true);
    }
    if (this._shakeReleaseTimer) {
      clearTimeout(this._shakeReleaseTimer);
      this._shakeReleaseTimer = null;
    }
    this.input.clearSource('motion');
  }

  get latest() {
    return Object.assign({}, this._latest);
  }

  // ─────────────────────────────────────────────────────────────── internals

  _onEvent(ev) {
    // Prefer plain `acceleration` (gravity removed) when present, fall back
    // to `accelerationIncludingGravity`. Both are objects of {x, y, z}.
    const a = ev.acceleration || ev.accelerationIncludingGravity || { x: 0, y: 0, z: 0 };
    const x = a.x || 0;
    const y = a.y || 0;
    const z = a.z || 0;
    const mag = Math.sqrt(x * x + y * y + z * z);

    this._latest = { x, y, z, magnitude: mag };
    this.feed(x, y, z);
  }

  /**
   * Public test/debug entry point: pretend a devicemotion event arrived
   * with the given acceleration values. Lets tests exercise the shake
   * detector without dispatching real DOM events.
   */
  feed(x, y, z) {
    const mag = Math.sqrt(x * x + y * y + z * z);
    this._latest = { x, y, z, magnitude: mag };

    this.input.setSourceAxis('motion', 'accelX', clamp(x / 20, -1, 1));
    this.input.setSourceAxis('motion', 'accelY', clamp(y / 20, -1, 1));
    this.input.setSourceAxis('motion', 'accelZ', clamp(z / 20, -1, 1));

    const now = Date.now();
    if (mag >= this.shakeThreshold && now - this._lastShakeAt >= this.shakeCooldownMs) {
      this._lastShakeAt = now;
      this.input.setSourceButton('motion', 'shake', true);
      if (this._shakeReleaseTimer) clearTimeout(this._shakeReleaseTimer);
      this._shakeReleaseTimer = setTimeout(() => {
        this.input.setSourceButton('motion', 'shake', false);
        this._shakeReleaseTimer = null;
      }, this.shakeHoldMs);
    }
  }
}

function clamp(v, lo, hi) {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
